/**
 * BioPintoSTP — Quinta Modelo Conectada
 * Digital twin dashboard for the São Tomé model farm integrated in the HannoNexus platform.
 *
 * Design tokens:
 *   --hn-neon: #0A9B5F (sage LED green, brand primary)
 *   --hn-cyan: #06B6D4 (IoT/AI data)
 *   --hn-solar: #F59E0B (solar/energy metrics)
 *   --hn-bg:   #0A0F1A (matte black)
 *   --hn-panel:#141A22 (graphite)
 *
 * Integration hooks:
 *   • <!-- INSERIR INFOGRÁFICO --> markers show where to paste real Chart.js / SVGs / D3 outputs.
 *   • All numeric values use data-count-target so the useCountUp hook animates them on mount.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  Activity, Sun, Droplets, Cpu, Satellite, ShieldCheck, Leaf, Flower2,
  Sprout, AlertTriangle, CalendarRange, GraduationCap, ArrowRight, Radio,
} from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";
import { Footer } from "@/components/hx/Footer";
import { useHannoContent } from "@/data/hanno-content";

const NEON = "#0A9B5F";
const CYAN = "#06B6D4";
const SOLAR = "#F59E0B";

// ============================================================================
//  Count-up hook — animates 0 → target on mount using requestAnimationFrame.
// ============================================================================
function useCountUp(target, { duration = 1600, decimals = 0, suffix = "" } = {}) {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  const formatted = decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString("pt-PT");
  return `${formatted}${suffix}`;
}

// ============================================================================
//  Metric — animated number card with icon + accent color
// ============================================================================
const Metric = ({ icon: Icon, label, value, unit = "", accent = NEON, decimals = 0, sub, testid }) => {
  const num = useCountUp(value, { decimals, suffix: unit });
  return (
    <div
      data-testid={testid}
      className="hx-glass rounded-xl px-5 py-5 border transition-all hover:-translate-y-0.5"
      style={{ borderColor: `${accent}22`, boxShadow: `0 0 0 1px ${accent}0F inset` }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{label}</div>
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}12` }}>
          <Icon size={13} style={{ color: accent }} strokeWidth={1.6} />
        </div>
      </div>
      <div className="mt-3 font-display font-bold text-3xl lg:text-4xl tabular-nums tracking-tight" style={{ color: accent }}>
        {num}
      </div>
      {sub && <div className="mt-1 text-[11px] text-hx-muted/80">{sub}</div>}
    </div>
  );
};

// ============================================================================
//  Sector card — for the 4-tile production matrix
// ============================================================================
const SectorCard = ({ icon: Icon, title, status, statusColor = NEON, kpiLabel, kpiValue, kpiSuffix = "%", accent = NEON, delay = 0, testid, image }) => {
  const pct = useCountUp(kpiValue, { duration: 1800 });
  const barPct = typeof kpiValue === "number" ? Math.min(100, kpiValue) : 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay }}
      data-testid={testid}
      className="hx-glass rounded-2xl overflow-hidden border relative group"
      style={{ borderColor: `${accent}22` }}
    >
      {/* Image header */}
      {image && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)",
          }} />
        </div>
      )}

      <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
           style={{ background: `radial-gradient(circle at 30% 0%, ${accent}0f 0%, transparent 60%)` }} />

      <div className="relative p-6 lg:p-7">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
            <Icon size={18} style={{ color: accent }} strokeWidth={1.6} />
          </div>
          <div className="font-display font-semibold text-lg tracking-tight">{title}</div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: statusColor }} />
          <span className="text-[11px] uppercase tracking-[0.22em] text-hx-muted">{status}</span>
        </div>

        <div className="mt-5">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{kpiLabel}</div>
            <div className="font-display font-bold text-2xl tabular-nums" style={{ color: accent }}>{pct}{kpiSuffix}</div>
          </div>
          {/* CSS progress bar — replace with real Chart.js sparkline if desired */}
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${barPct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: delay + 0.2 }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}80 100%)`, boxShadow: `0 0 12px ${accent}80` }}
            />
          </div>
        </div>
      </div>
    </motion.article>
  );
};

// ============================================================================
//  Recent AI alerts — simulates decisions taken by the HN AI layer
// ============================================================================
const AI_ALERTS = [
  {
    time: "há 4 min",
    severity: "climate",
    icon: Satellite,
    title: "Alerta Climático — Seca detetada por satélite CropWatch",
    action: "Irrigação Haishun ajustada automaticamente: -18% consumo hídrico nos setores B3/B4.",
  },
  {
    time: "há 12 min",
    severity: "energy",
    icon: Sun,
    title: "Pico solar acima da média sazonal (+22%)",
    action: "Excedente redirecionado para bombagem DIFFUL — armazenamento de água na cisterna superior.",
  },
  {
    time: "há 38 min",
    severity: "bio",
    icon: Leaf,
    title: "Humidade do solo — Setor Hortícolas C1 em estado ideal",
    action: "Ciclo de rega suspenso; economia estimada de 340 L nas próximas 6 h.",
  },
];

// ============================================================================
//  Harvest forecast — timeline generated by HN AI
// ============================================================================
const HARVEST_FORECAST = [
  { week: "Semana 24", crop: "Alface Batávia (Setor C2)", volume: "180 kg", channel: "Mercado Local · STP-Sul", confidence: 96 },
  { week: "Semana 26", crop: "Flores Ornamentais — Antúrios", volume: "12 lotes", channel: "Exportação · Lisboa (LIS)", confidence: 91 },
  { week: "Semana 27", crop: "Tomate Cereja (Setor C4)", volume: "240 kg", channel: "Cadeia Hoteleira · São Tomé", confidence: 88 },
  { week: "Semana 28", crop: "Ovos Caipira (Aviário A1)", volume: "3.400 un.", channel: "Distribuição Contínua", confidence: 99 },
];

// ============================================================================
//  Multi-client ecosystem — who consumes what
// ============================================================================
const CLIENT_ECOSYSTEM = [
  { group: "Agricultores Locais", consume: "Formação Técnica + Produção", pct: 34, icon: Sprout },
  { group: "Escolas & Universidades", consume: "Dias de Campo + Currículo IoT", pct: 22, icon: GraduationCap },
  { group: "Municípios & Governo", consume: "Modelo Replicável + Dados", pct: 18, icon: ShieldCheck },
  { group: "ONGs & Cooperação", consume: "Tecnologia + Regeneração", pct: 26, icon: Leaf },
];

// ============================================================================
//  MAIN PAGE COMPONENT
// ============================================================================
export default function BioPintoPage() {
  const c = useHannoContent();
  const t = c.dashboards.biopinto;
  const cc = c.dashboards.common;
  return (
    <div className="App relative bg-hx-bg text-hx-text" data-testid="page-biopinto">
      <Navbar />

      {/* Ambient background — cyan + neon glow */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 hx-grid-bg opacity-[0.16]" />
        <div className="absolute top-0 left-1/4 w-[520px] h-[520px] rounded-full blur-3xl opacity-[0.12]"
             style={{ background: `radial-gradient(circle, ${NEON} 0%, transparent 70%)` }} />
        <div className="absolute top-1/3 right-0 w-[480px] h-[480px] rounded-full blur-3xl opacity-[0.09]"
             style={{ background: `radial-gradient(circle, ${CYAN} 0%, transparent 70%)` }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-3xl opacity-[0.06]"
             style={{ background: `radial-gradient(circle, ${SOLAR} 0%, transparent 70%)` }} />
      </div>

      <main className="relative">
        {/* ==================================================================== */}
        {/*  1. HERO & STATUS GERAL                                              */}
        {/* ==================================================================== */}
        <section className="relative pt-32 lg:pt-40 pb-14 lg:pb-20 overflow-hidden" data-testid="biopinto-hero">
          {/* Hero background image */}
          <div className="absolute inset-0 -z-10">
            <img
              src="/hanno/biopinto/biopinto-hero.png"
              alt="Vista aérea da quinta modelo BioPintoSTP em São Tomé"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{
              background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 45%, rgba(10,15,26,0.90) 100%)",
            }} />
            <div className="absolute inset-0" style={{
              background: `radial-gradient(circle at 70% 30%, ${NEON}0d 0%, transparent 55%)`,
            }} />
          </div>
          <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {/* Live status badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border"
                   style={{ background: `${NEON}0f`, borderColor: `${NEON}44` }}
                   data-testid="biopinto-status-badge">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: NEON }} />
                  <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: NEON }} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>
                  {cc.online}
                </span>
              </div>

              <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                {t.title_a}{" "}
                <span style={{ color: NEON }}>—</span>{" "}
                <span className="block sm:inline">{t.title_b}</span>
              </h1>
              <p className="mt-5 max-w-3xl text-base lg:text-lg text-hx-muted leading-relaxed">
                {t.lede}
              </p>

              {/* Top-line telemetry (animated) */}
              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric icon={Sun}       label={t.kpi_solar}  value={45}   unit=" kW" accent={SOLAR} sub={t.kpi_solar_sub} testid="metric-solar" />
                <Metric icon={Droplets}  label={t.kpi_water}  value={30}   unit="%"   accent={CYAN}  sub={t.kpi_water_sub} testid="metric-water" />
                <Metric icon={Cpu}       label={t.kpi_nodes}  value={128}  unit=""    accent={NEON}  sub={t.kpi_nodes_sub} testid="metric-nodes" />
                <Metric icon={Satellite} label={t.kpi_sat}    value={6}    unit="/dia" accent={CYAN} sub={t.kpi_sat_sub}   testid="metric-sat" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  2. CAMADA FÍSICA — LOOP DE ENERGIA E ÁGUA                          */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="biopinto-loop">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow={t.loop_eyebrow}
              title={t.loop_title}
              accent={SOLAR}
            />

            <div className="mt-10 grid lg:grid-cols-2 gap-6 lg:gap-8">
              {/* --- Coluna Esquerda: Fotografia real do loop energia/água --- */}
              <div
                className="relative rounded-2xl overflow-hidden min-h-[380px] lg:min-h-[440px] border group"
                style={{ borderColor: `${SOLAR}30` }}
                data-testid="loop-infographic-placeholder"
              >
                <img
                  src="/hanno/biopinto/biopinto-loop-energy-water.png"
                  alt="Loop físico Energia ↔ Água: painel solar, bomba DIFFUL, inversor e culturas irrigadas"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)",
                }} />
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: `radial-gradient(circle at 20% 20%, ${SOLAR}18 0%, transparent 55%), radial-gradient(circle at 80% 80%, ${CYAN}12 0%, transparent 55%)`,
                }} />
                {/* Overlay label */}
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border backdrop-blur-xl" style={{ background: "rgba(10,15,26,0.90)", borderColor: `${SOLAR}44` }}>
                    <Activity size={11} style={{ color: SOLAR }} strokeWidth={1.7} />
                    <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: SOLAR }}>
                      {t.loop_badge}
                    </span>
                  </div>
                  <div className="mt-2 font-display font-semibold text-lg text-white" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>
                    {t.loop_overlay_title}
                  </div>
                  <div className="mt-0.5 text-xs text-white/70" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>
                    {t.loop_overlay_sub}
                  </div>
                </div>
              </div>

              {/* --- Coluna Direita: Cards de telemetria --- */}
              <div className="grid gap-5">
                <TelemetryCard
                  accent={SOLAR}
                  icon={Sun}
                  brand={t.tel_solar_brand}
                  title={t.tel_solar_title}
                  rows={[
                    [t.tel_solar_row1, <SpanCount key="g" value={45} suffix=" kW" accent={SOLAR} />],
                    [t.tel_solar_row2, <span key="d" className="font-mono font-semibold" style={{ color: NEON }}>{t.tel_solar_row2_val}</span>],
                    [t.tel_solar_row3, <SpanCount key="a" value={312} suffix=" kWh" accent={SOLAR} />],
                  ]}
                  testid="telemetry-solar"
                />
                <TelemetryCard
                  accent={CYAN}
                  icon={Droplets}
                  brand={t.tel_water_brand}
                  title={t.tel_water_title}
                  rows={[
                    [t.tel_water_row1, <span key="f" className="font-mono font-semibold" style={{ color: CYAN }}>{t.tel_water_row1_val}</span>],
                    [t.tel_water_row2, <SpanCount key="p" value={30} suffix=" %" accent={CYAN} />],
                    [t.tel_water_row3, <SpanCount key="v" value={87} suffix=" %" accent={CYAN} />],
                  ]}
                  testid="telemetry-water"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  3. CAMADA DE PROCESSAMENTO — MATRIZ DE PRODUÇÃO IOT                */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="biopinto-matrix">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow={t.matrix_eyebrow}
              title={t.matrix_title}
              accent={NEON}
            />

            {/*
              ============================================================
              OPCIONAL: substituir os SectorCards por gráficos reais
              (ex.: Chart.js doughnut ou radar) inserindo-os dentro de cada card.
              ============================================================
            */}
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <SectorCard
                icon={ShieldCheck}
                title={t.sec_avi}
                status={t.sec_avi_status}
                statusColor={NEON}
                kpiLabel={t.sec_avi_kpi}
                kpiValue={100}
                accent={NEON}
                delay={0}
                testid="sector-avicultura"
                image="/hanno/biopinto/biopinto-sector-avicultura.png"
              />
              <SectorCard
                icon={Sprout}
                title={t.sec_viv}
                status={t.sec_viv_status}
                statusColor={CYAN}
                kpiLabel={t.sec_viv_kpi}
                kpiValue={85}
                accent={CYAN}
                delay={0.1}
                testid="sector-viveiro"
                image="/hanno/biopinto/biopinto-sector-viveiro.png"
              />
              <SectorCard
                icon={Flower2}
                title={t.sec_flo}
                status={t.sec_flo_status}
                statusColor={SOLAR}
                kpiLabel={t.sec_flo_kpi}
                kpiValue={12}
                kpiSuffix=" un"
                accent={SOLAR}
                delay={0.2}
                testid="sector-flores"
                image="/hanno/biopinto/biopinto-sector-flores.png"
              />
              <SectorCard
                icon={Leaf}
                title={t.sec_hor}
                status={t.sec_hor_status}
                statusColor={NEON}
                kpiLabel={t.sec_hor_kpi}
                kpiValue={72}
                accent={NEON}
                delay={0.3}
                testid="sector-horticolas"
                image="/hanno/biopinto/biopinto-sector-horticolas.png"
              />
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  4. CAMADA DE INTERFACE — PREVISÃO E ALERTAS                        */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="biopinto-forecast">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow={t.forecast_eyebrow}
              title={t.forecast_title}
              accent={CYAN}
            />

            <div className="mt-10 grid lg:grid-cols-[1fr_1.3fr] gap-6 lg:gap-8">
              {/* Recent AI Alerts */}
              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${CYAN}22` }} data-testid="ai-alerts">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${CYAN}14`, border: `1px solid ${CYAN}30` }}>
                    <Radio size={16} style={{ color: CYAN }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{cc.recent_alerts}</div>
                    <div className="font-display font-semibold text-lg tracking-tight">{cc.live_feed}</div>
                  </div>
                </div>

                <ul className="space-y-4">
                  {t.alerts.map((a, i) => {
                    const src = AI_ALERTS[i] || {};
                    return (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="flex gap-3 pb-4 border-b border-white/[0.10] last:border-0 last:pb-0"
                    >
                      <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center" style={{ background: severityBg(src.severity), border: `1px solid ${severityBorder(src.severity)}` }}>
                        {src.icon && <src.icon size={13} style={{ color: severityColor(src.severity) }} strokeWidth={1.6} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-hx-text">{a.title}</div>
                          <span className="text-[9px] uppercase tracking-[0.2em] text-hx-muted/70 font-mono">{a.time}</span>
                        </div>
                        <div className="mt-1.5 text-xs text-hx-muted leading-relaxed">{a.action}</div>
                      </div>
                    </motion.li>
                    );
                  })}
                </ul>
              </div>

              {/* Harvest Forecast Timeline / Table */}
              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${NEON}22` }} data-testid="harvest-forecast">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${NEON}14`, border: `1px solid ${NEON}30` }}>
                    <CalendarRange size={16} style={{ color: NEON }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{t.forecast_head}</div>
                    <div className="font-display font-semibold text-lg tracking-tight">{cc.ai_recommended}</div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="forecast-table">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-[0.22em] text-hx-muted border-b border-white/[0.10]">
                        <th className="text-left font-medium py-2 pr-3">{t.col_window}</th>
                        <th className="text-left font-medium py-2 pr-3">{t.col_crop}</th>
                        <th className="text-left font-medium py-2 pr-3 hidden sm:table-cell">{t.col_volume}</th>
                        <th className="text-left font-medium py-2 pr-3 hidden md:table-cell">{t.col_channel}</th>
                        <th className="text-right font-medium py-2">{t.col_confidence}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.harvest.map((row, i) => {
                        const conf = HARVEST_FORECAST[i]?.confidence ?? 95;
                        return (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: i * 0.08 }}
                          className="border-b border-white/[0.10] last:border-0"
                        >
                          <td className="py-3 pr-3 font-mono text-xs text-hx-muted">{row.window}</td>
                          <td className="py-3 pr-3 text-hx-text">{row.crop}</td>
                          <td className="py-3 pr-3 hidden sm:table-cell text-hx-muted">{row.volume}</td>
                          <td className="py-3 pr-3 hidden md:table-cell text-hx-muted">{row.channel}</td>
                          <td className="py-3 text-right">
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold px-2 py-0.5 rounded"
                              style={{ background: `${NEON}12`, color: NEON, border: `1px solid ${NEON}30` }}
                            >
                              {conf}%
                            </span>
                          </td>
                        </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================== */}
        {/*  5. IMPACTO SOCIAL & MODELO MULTICLIENTE                            */}
        {/* ==================================================================== */}
        <section className="relative py-14 lg:py-20 border-t border-white/[0.10]" data-testid="biopinto-impact">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow={t.impact_eyebrow}
              title={t.impact_title}
              accent={SOLAR}
            />

            <div className="mt-10 grid lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-8">
              {/* Dias de Campo — placeholder editorial */}
              <div className="hx-glass rounded-2xl p-6 lg:p-8 border" style={{ borderColor: `${SOLAR}22` }} data-testid="dias-de-campo">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${SOLAR}14`, border: `1px solid ${SOLAR}30` }}>
                    <GraduationCap size={18} style={{ color: SOLAR }} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{t.academy_eyebrow}</div>
                    <div className="font-display font-semibold text-xl tracking-tight">{t.academy_title}</div>
                  </div>
                </div>

                <p className="text-sm text-hx-muted leading-relaxed">
                  {t.academy_lede}
                </p>

                <div
                  className="mt-6 relative rounded-xl overflow-hidden aspect-[16/9] border group"
                  style={{ borderColor: `${SOLAR}30` }}
                  data-testid="dias-de-campo-photo"
                >
                  <img
                    src="/hanno/biopinto/biopinto-dias-de-campo.png"
                    alt={t.academy_photo_caption}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0" style={{
                    background: "linear-gradient(180deg, rgba(10,15,26,0.90) 0%, rgba(10,15,26,0.90) 100%)",
                  }} />
                  <div className="absolute bottom-4 left-5 right-5">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border backdrop-blur-xl" style={{ background: "rgba(10,15,26,0.90)", borderColor: `${SOLAR}44` }}>
                      <GraduationCap size={11} style={{ color: SOLAR }} strokeWidth={1.7} />
                      <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: SOLAR }}>
                        {t.academy_photo_tag}
                      </span>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-white" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>
                      {t.academy_photo_caption}
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <MiniStat label={t.mini_participants} value={247} accent={SOLAR} />
                  <MiniStat label={t.mini_sessions}     value={9}   accent={NEON} />
                  <MiniStat label={t.mini_schools}      value={4}   accent={CYAN} />
                </div>
              </div>

              {/* Client ecosystem */}
              <div className="hx-glass rounded-2xl p-6 lg:p-7 border" style={{ borderColor: `${NEON}22` }} data-testid="client-ecosystem">
                <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{cc.client_ecosystem}</div>
                <div className="mt-1 font-display font-semibold text-xl tracking-tight">{cc.who_consumes}</div>

                <ul className="mt-6 space-y-4">
                  {t.clients.map((cli, i) => {
                    const src = CLIENT_ECOSYSTEM[i] || {};
                    return <ClientRow key={cli.group} row={{ ...cli, pct: src.pct, icon: src.icon }} delay={i * 0.1} />;
                  })}
                </ul>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-14 hx-glass-strong rounded-2xl p-8 lg:p-10 border text-center" style={{ borderColor: `${NEON}30` }}>
              <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: NEON }}>{cc.replicability}</div>
              <h3 className="mt-3 font-display font-bold text-2xl lg:text-3xl tracking-tight">
                {t.cta_title}
              </h3>
              <p className="mt-3 text-sm text-hx-muted max-w-2xl mx-auto">
                {t.cta_lede}
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <a
                  href="/contact"
                  className="btn-hx-primary inline-flex items-center gap-2"
                  data-testid="biopinto-cta-partner"
                >
                  {cc.request_meeting} <ArrowRight size={14} />
                </a>
                <a
                  href="/projects"
                  className="text-[11px] uppercase tracking-[0.24em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2"
                  data-testid="biopinto-cta-projects"
                >
                  {cc.other_projects} <ArrowRight size={12} />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ============================================================================
//  Helper components (co-located to keep the page self-contained)
// ============================================================================
const SectionHeader = ({ eyebrow, title, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>{eyebrow}</div>
    <h2 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-3xl leading-tight">{title}</h2>
  </div>
);

const TelemetryCard = ({ accent, icon: Icon, brand, title, rows, testid }) => (
  <div className="hx-glass rounded-xl p-5 lg:p-6 border" style={{ borderColor: `${accent}22` }} data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${accent}14`, border: `1px solid ${accent}30` }}>
        <Icon size={16} style={{ color: accent }} strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.24em] text-hx-muted">{brand}</div>
        <div className="font-display font-semibold text-base tracking-tight">{title}</div>
      </div>
    </div>
    <dl className="mt-4 space-y-2.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-baseline justify-between gap-4 border-b border-white/[0.10] last:border-0 pb-2 last:pb-0">
          <dt className="text-xs text-hx-muted">{k}</dt>
          <dd className="text-sm text-hx-text">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const SpanCount = ({ value, suffix, accent }) => {
  const num = useCountUp(value, { suffix });
  return <span className="font-mono font-semibold tabular-nums" style={{ color: accent }}>{num}</span>;
};

const MiniStat = ({ label, value, accent }) => {
  const num = useCountUp(value);
  return (
    <div className="rounded-lg px-3 py-3 border" style={{ borderColor: `${accent}22`, background: `${accent}06` }}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted">{label}</div>
      <div className="mt-1 font-display font-bold text-xl tabular-nums" style={{ color: accent }}>{num}</div>
    </div>
  );
};

const ClientRow = ({ row, delay }) => {
  const pct = useCountUp(row.pct, { suffix: "%" });
  return (
    <motion.li
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: `${NEON}12`, border: `1px solid ${NEON}30` }}>
          <row.icon size={14} style={{ color: NEON }} strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-semibold text-hx-text truncate">{row.group}</div>
            <div className="text-xs font-mono font-semibold tabular-nums" style={{ color: NEON }}>{pct}</div>
          </div>
          <div className="mt-0.5 text-[11px] text-hx-muted truncate">{row.consume}</div>
          <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${row.pct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, delay: delay + 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full"
              style={{ background: NEON, boxShadow: `0 0 8px ${NEON}80` }}
            />
          </div>
        </div>
      </div>
    </motion.li>
  );
};

// Severity color helpers (AI alerts)
function severityColor(s) {
  if (s === "climate") return CYAN;
  if (s === "energy") return SOLAR;
  return NEON;
}
function severityBg(s) { return `${severityColor(s)}14`; }
function severityBorder(s) { return `${severityColor(s)}30`; }
